import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowLeft,
  FaBolt,
  FaChartLine,
  FaCrown,
  FaFileAlt,
  FaFolderOpen,
  FaTrophy,
  FaUserClock,
  FaUserSlash,
  FaUsers,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { KpiCard } from "./components/KpiCard";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { PartialDataBanner } from "./components/PartialDataBanner";
import { HealthBadge } from "./components/HealthBadge";
import { Avatar } from "./components/Avatar";
import { EnvToggle } from "./components/EnvToggle";
import { useEnv } from "./useEnv";
import { dateRangeParams, fetchOrgDashboard } from "./api";
import "./OrgUsageAnalytics.css";

const formatRelative = (ms) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return "—";
  }
};

const fmtN = (n) =>
  typeof n === "number" ? n.toLocaleString() : n ?? "0";

function getActivityStatus(m) {
  const now = Date.now();
  const lastMs =
    m.lastActiveAt ||
    (m.lastSignInTime ? new Date(m.lastSignInTime).getTime() : null);
  if (!lastMs) return "dormant";
  const days = (now - lastMs) / 86400000;
  if (days <= 7) return "active";
  if (days <= 30) return "idle";
  return "dormant";
}

const chartTooltipProps = {
  contentStyle: {
    background: "#fff",
    border: "1px solid #e6e9f1",
    borderRadius: 8,
    fontSize: 12,
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
  },
  labelStyle: { color: "#64748b", fontWeight: 600, fontSize: 11 },
};

function SkeletonKpis() {
  return (
    <div className="ou-kpi-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="ou-kpi">
          <div className="ou-skel ou-skel-line" style={{ width: "55%" }} />
          <div
            className="ou-skel"
            style={{ height: 28, width: "45%", margin: "10px 0 6px" }}
          />
          <div className="ou-skel ou-skel-line" style={{ width: "40%" }} />
        </div>
      ))}
    </div>
  );
}

export default function OrgUsageDetailPage() {
  const { organizationId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const orgName = location.state?.organizationName || organizationId;

  const [env, setEnv] = useEnv();
  // Default to 7 days: fewer history rows to read = faster first load. Wider
  // windows are still one click away via DateRangeFilter.
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [partial, setPartial] = useState(false);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [points, setPoints] = useState([]);
  const [features, setFeatures] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [recentAccess, setRecentAccess] = useState([]);

  const range = useMemo(() => dateRangeParams(days), [days]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Single combined call (replaces 6 parallel requests). Backend
      // shares Firestore reads across all aggregations, so the dashboard
      // loads ~3-4x faster and at a fraction of the read cost.
      const data = await fetchOrgDashboard(organizationId, range, env);
      setSummary(data.summary || null);
      setMembers(data.members?.members || []);
      setPoints(data.usageOverTime?.points || []);
      setFeatures((data.featureBreakdown?.features || []).slice(0, 8));
      setBuckets(data.usersByActivity?.buckets || []);
      setRecentAccess((data.recentLoanAccess?.entries || []).slice(0, 10));
      setPartial(Boolean(data.partial));
    } catch (err) {
      toast.error(err.message || "Failed to load org usage");
    } finally {
      setLoading(false);
    }
  }, [organizationId, range, env]);

  useEffect(() => {
    load();
  }, [load]);

  const adoptionPct =
    summary?.totalMembers > 0
      ? Math.round((summary.activeUsers / summary.totalMembers) * 1000) / 10
      : 0;

  const topFeature = features[0];
  const topUser = summary?.mostActiveUsers?.[0];
  const avgEventsPerUser =
    summary?.activeUsers > 0
      ? Math.round((summary.totalEvents / summary.activeUsers) * 10) / 10
      : 0;

  const bucketMap = Object.fromEntries(
    (buckets || []).map((b) => [b.bucket, b.count]),
  );

  return (
    <div className="ou-page">
      <ToastContainer position="top-right" autoClose={4000} />

      <nav className="ou-breadcrumb">
        <Link to="/org-usage-analytics">User Usage</Link>
        <span className="ou-breadcrumb-sep">/</span>
        <span style={{ color: "var(--ou-text)", fontWeight: 500 }}>
          {orgName}
        </span>
      </nav>
      <button
        type="button"
        className="ou-back-btn"
        onClick={() => history.push("/org-usage-analytics")}
      >
        <FaArrowLeft /> All organizations
      </button>

      <header className="ou-header">
        <div className="ou-header-left ou-profile-strip">
          <Avatar name={orgName} seed={organizationId} size="lg" />
          <div className="ou-profile-meta">
            <h1 className="ou-profile-name">{orgName}</h1>
            <div className="ou-profile-sub">
              <span className="ou-mono">{organizationId}</span>
              <span className="ou-dot" />
              <span>{fmtN(summary?.totalMembers)} members</span>
              {summary?.activeUsers != null ? (
                <>
                  <span className="ou-dot" />
                  <span>{fmtN(summary.activeUsers)} active</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <EnvToggle env={env} onChange={setEnv} />
          <DateRangeFilter days={days} onChange={setDays} />
        </div>
      </header>

      <PartialDataBanner show={partial} />

      {loading && !summary ? (
        <SkeletonKpis />
      ) : (
        <>
          <div className="ou-kpi-grid">
            <KpiCard
              label="Active users"
              value={fmtN(summary?.activeUsers)}
              icon={<FaUsers />}
              accent="indigo"
              subtext="With ≥1 event in period"
            />
            <KpiCard
              label="Total members"
              value={fmtN(summary?.totalMembers)}
              icon={<FaUsers />}
              accent="cyan"
              subtext="Org seats in scope"
            />
            <KpiCard
              label="Adoption"
              value={`${adoptionPct}%`}
              icon={<FaBolt />}
              accent="green"
              subtext="Active / total"
            />
            <KpiCard
              label="Total events"
              value={fmtN(summary?.totalEvents)}
              icon={<FaChartLine />}
              accent="pink"
              subtext="Genuine user events (excl. autoruns)"
            />
            <KpiCard
              label="Loans accessed"
              value={fmtN(summary?.totalLoansAccessed)}
              icon={<FaFolderOpen />}
              accent="amber"
              subtext="Unique loans opened"
            />
            <KpiCard
              label="Lagging users"
              value={fmtN(summary?.laggingUsers)}
              icon={<FaUserSlash />}
              accent="rose"
              subtext="Members with 0 events"
            />
          </div>

          <div className="ou-highlight-grid">
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--indigo">
                <FaCrown />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Top user</div>
                <div
                  className="ou-highlight-value"
                  title={topUser?.name || topUser?.email}
                >
                  {topUser?.name || topUser?.email || "—"}
                </div>
                <div className="ou-highlight-sub">
                  {topUser?.eventCount != null
                    ? `${fmtN(topUser.eventCount)} events`
                    : "No activity"}
                </div>
              </div>
            </div>
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--pink">
                <FaTrophy />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Top feature</div>
                <div
                  className="ou-highlight-value"
                  title={topFeature?.feature}
                >
                  {topFeature?.feature || "—"}
                </div>
                <div className="ou-highlight-sub">
                  {topFeature?.count != null
                    ? `${fmtN(topFeature.count)} events`
                    : "No usage"}
                </div>
              </div>
            </div>
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--green">
                <FaChartLine />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Events / active user</div>
                <div className="ou-highlight-value">{avgEventsPerUser}</div>
                <div className="ou-highlight-sub">Activity intensity</div>
              </div>
            </div>
            <div className="ou-highlight">
              <span className="ou-highlight-icon ou-highlight-icon--amber">
                <FaUserClock />
              </span>
              <div className="ou-highlight-body">
                <div className="ou-highlight-label">Active · Idle · Dormant</div>
                <div className="ou-highlight-value">
                  {bucketMap.active ?? 0} · {bucketMap.idle ?? 0} ·{" "}
                  {bucketMap.dormant ?? 0}
                </div>
                <div className="ou-highlight-sub">7d / 30d / 30d+ buckets</div>
              </div>
            </div>
          </div>

          <div className="ou-charts-row">
            <section className="ou-panel">
              <div className="ou-panel-head">
                <div>
                  <h3 className="ou-panel-title">
                    <FaChartLine className="ou-panel-title-icon" />
                    Usage over time
                  </h3>
                  <p className="ou-panel-sub">
                    Daily events and unique active users.
                  </p>
                </div>
              </div>
              <div className="ou-panel-body">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart
                    data={points}
                    margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip {...chartTooltipProps} />
                    <Line
                      type="monotone"
                      dataKey="events"
                      stroke="#4f46e5"
                      strokeWidth={2.5}
                      dot={false}
                      name="Events"
                    />
                    <Line
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#ec4899"
                      strokeWidth={2.5}
                      dot={false}
                      name="Active users"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="ou-panel">
              <div className="ou-panel-head">
                <div>
                  <h3 className="ou-panel-title">
                    <FaBolt className="ou-panel-title-icon" />
                    Feature breakdown
                  </h3>
                  <p className="ou-panel-sub">
                    Most-used features in the selected period.
                  </p>
                </div>
              </div>
              <div className="ou-panel-body">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={features}
                    layout="vertical"
                    margin={{ left: 20, right: 16, top: 4, bottom: 4 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#eef2f7"
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="feature"
                      tick={{ fontSize: 11, fill: "#475569" }}
                      width={140}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip {...chartTooltipProps} cursor={{ fill: "#f8fafc" }} />
                    <Bar
                      dataKey="count"
                      fill="#7F7FD5"
                      radius={[0, 6, 6, 0]}
                      barSize={18}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          <section className="ou-panel">
            <div className="ou-panel-head">
              <div>
                <h3 className="ou-panel-title">
                  <FaUsers className="ou-panel-title-icon" />
                  Team members
                </h3>
                <p className="ou-panel-sub">
                  Click any row to see a personal usage profile.
                </p>
              </div>
              <span className="ou-mono" style={{ alignSelf: "center" }}>
                {members.length} members
              </span>
            </div>
            <div className="ou-panel-body ou-panel-body--flush">
              <div className="ou-table-wrap">
                <table className="ou-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last active</th>
                      <th style={{ textAlign: "right" }}>Events</th>
                      <th style={{ textAlign: "right" }} title="Distinct loans opened/viewed">
                        Loans opened
                      </th>
                      <th style={{ textAlign: "right" }} title="Manual AI Agent runs (autoruns excluded)">
                        Manual runs
                      </th>
                      <th style={{ textAlign: "right" }}>Uploads</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {[...members]
                      .sort((a, b) => (b.totalEvents || 0) - (a.totalEvents || 0))
                      .map((m) => (
                        <tr
                          key={m.userId || m.email}
                          className={m.userId ? "is-clickable" : ""}
                          onClick={() =>
                            m.userId &&
                            history.push(
                              `/org-usage-analytics/${organizationId}/users/${m.userId}`,
                              {
                                organizationName: orgName,
                                userName: m.name,
                                userEmail: m.email,
                              },
                            )
                          }
                        >
                          <td>
                            <div className="ou-table-user">
                              <Avatar
                                name={m.name || m.email}
                                seed={m.userId || m.email}
                                size="sm"
                              />
                              <div style={{ minWidth: 0 }}>
                                <div className="ou-table-user-name">
                                  {m.name || m.email || "—"}
                                </div>
                                <div className="ou-table-user-email">
                                  {m.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            {m.role ? (
                              <span className="ou-role-pill">{m.role}</span>
                            ) : (
                              <span style={{ color: "var(--ou-dim)" }}>—</span>
                            )}
                          </td>
                          <td>
                            <HealthBadge health={getActivityStatus(m)} />
                          </td>
                          <td style={{ color: "var(--ou-muted)", fontSize: 12 }}>
                            {formatRelative(m.lastActiveAt)}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span className="ou-num">
                              {fmtN(m.totalEvents ?? 0)}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span
                              className={
                                m.loansAccessedCount
                                  ? "ou-num"
                                  : "ou-num ou-num--muted"
                              }
                            >
                              {m.loansAccessedCount ?? 0}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span
                              className={
                                m.checklistRunsCount
                                  ? "ou-num"
                                  : "ou-num ou-num--muted"
                              }
                            >
                              {m.checklistRunsCount ?? 0}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span
                              className={
                                m.documentsUploadedCount
                                  ? "ou-num"
                                  : "ou-num ou-num--muted"
                              }
                            >
                              {m.documentsUploadedCount ?? 0}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {m.userId ? (
                              <span className="ou-link-btn">View →</span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {recentAccess.length > 0 && (
            <section className="ou-panel">
              <div className="ou-panel-head">
                <div>
                  <h3 className="ou-panel-title">
                    <FaFileAlt className="ou-panel-title-icon" />
                    Recent loan access
                  </h3>
                  <p className="ou-panel-sub">
                    Latest loan_opened events from team members.
                  </p>
                </div>
              </div>
              <div className="ou-panel-body ou-panel-body--flush">
                <div className="ou-table-wrap">
                  <table className="ou-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Loan</th>
                        <th style={{ textAlign: "right" }}>Opened</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentAccess.map((row, i) => (
                        <tr key={`${row.userId}-${row.loanId}-${i}`}>
                          <td>
                            <div className="ou-table-user">
                              <Avatar
                                name={row.userName || row.userEmail}
                                seed={row.userId}
                                size="sm"
                              />
                              <div>
                                <div className="ou-table-user-name">
                                  {row.userName || row.userEmail || "—"}
                                </div>
                                {row.userEmail && row.userName ? (
                                  <div className="ou-table-user-email">
                                    {row.userEmail}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span style={{ fontWeight: 500 }}>
                              {row.loanLabel || row.loanId}
                            </span>
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              color: "var(--ou-muted)",
                              fontSize: 12,
                            }}
                          >
                            {formatRelative(row.accessedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
